// SVG → array of subpaths (each subpath = array of {x, y}).
// Handles: <path d="...">, <circle>, <rect>, <polygon>, <polyline>, <line>, <ellipse>.
// Rejects: text, embedded images, gradients (geometry only).
// Zero deps. Regex-based — no XML parser needed.

import { flattenCubic, flattenQuadratic, flattenArc } from './bezier.js';

const ATTR_RE = /([\w-]+)\s*=\s*["']([^"']*)["']/g;

function parseAttrs(s) {
  const out = {};
  let m;
  ATTR_RE.lastIndex = 0;
  while ((m = ATTR_RE.exec(s)) !== null) {
    out[m[1]] = m[2];
  }
  return out;
}

// Tokenize SVG path d-attribute → mixed array of strings (cmds) + numbers.
function tokenizePath(d) {
  const tokens = [];
  // Insert spaces around command letters
  const padded = d.replace(/([MmLlHhVvCcSsQqTtAaZz])/g, ' $1 ');
  // Split on whitespace OR commas, then reparse numbers
  const raw = padded.split(/[\s,]+/).filter(Boolean);
  // SVG number can have signs glued: -3.4-2 → ['-3.4', '-2']; handle 'e' notation
  const numRe = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;
  for (const t of raw) {
    if (/^[MmLlHhVvCcSsQqTtAaZz]$/.test(t)) {
      tokens.push(t);
    } else {
      let m;
      numRe.lastIndex = 0;
      while ((m = numRe.exec(t)) !== null) tokens.push(parseFloat(m[0]));
    }
  }
  return tokens;
}

function flattenPath(d, flatness) {
  const tokens = tokenizePath(d);
  const subpaths = [];
  let current = [];
  let cur = { x: 0, y: 0 };
  let start = { x: 0, y: 0 };
  let prevCubicCtrl = null;
  let prevQuadCtrl = null;
  let cmd = null;
  let i = 0;

  function pushPt(x, y) {
    current.push({ x, y });
  }
  function startSub(x, y) {
    if (current.length) subpaths.push(current);
    current = [{ x, y }];
    start = { x, y };
  }

  while (i < tokens.length) {
    if (typeof tokens[i] === 'string') {
      cmd = tokens[i++];
    }
    const isRel = cmd === cmd.toLowerCase();
    const c = cmd.toUpperCase();

    if (c === 'Z') {
      if (current.length && (current[0].x !== cur.x || current[0].y !== cur.y)) {
        pushPt(start.x, start.y);
      }
      cur = { ...start };
      prevCubicCtrl = null;
      prevQuadCtrl = null;
      // Don't consume any operands; loop will pick up next command
      continue;
    }
    if (typeof tokens[i] !== 'number') {
      // Unknown — skip
      continue;
    }

    switch (c) {
      case 'M': {
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x += cur.x; y += cur.y; }
        startSub(x, y);
        cur = { x, y };
        // After M, additional pairs are implicit L (per spec)
        cmd = isRel ? 'l' : 'L';
        break;
      }
      case 'L': {
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x += cur.x; y += cur.y; }
        pushPt(x, y);
        cur = { x, y };
        prevCubicCtrl = null; prevQuadCtrl = null;
        break;
      }
      case 'H': {
        let x = tokens[i++];
        if (isRel) x += cur.x;
        pushPt(x, cur.y);
        cur = { x, y: cur.y };
        prevCubicCtrl = null; prevQuadCtrl = null;
        break;
      }
      case 'V': {
        let y = tokens[i++];
        if (isRel) y += cur.y;
        pushPt(cur.x, y);
        cur = { x: cur.x, y };
        prevCubicCtrl = null; prevQuadCtrl = null;
        break;
      }
      case 'C': {
        let x1 = tokens[i++], y1 = tokens[i++];
        let x2 = tokens[i++], y2 = tokens[i++];
        let x = tokens[i++], y = tokens[i++];
        if (isRel) {
          x1 += cur.x; y1 += cur.y;
          x2 += cur.x; y2 += cur.y;
          x += cur.x; y += cur.y;
        }
        const seg = flattenCubic(cur.x, cur.y, x1, y1, x2, y2, x, y, flatness);
        for (let k = 1; k < seg.length; k++) pushPt(seg[k].x, seg[k].y);
        prevCubicCtrl = { x: x2, y: y2 };
        prevQuadCtrl = null;
        cur = { x, y };
        break;
      }
      case 'S': {
        let x2 = tokens[i++], y2 = tokens[i++];
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x2 += cur.x; y2 += cur.y; x += cur.x; y += cur.y; }
        const x1 = prevCubicCtrl ? 2 * cur.x - prevCubicCtrl.x : cur.x;
        const y1 = prevCubicCtrl ? 2 * cur.y - prevCubicCtrl.y : cur.y;
        const seg = flattenCubic(cur.x, cur.y, x1, y1, x2, y2, x, y, flatness);
        for (let k = 1; k < seg.length; k++) pushPt(seg[k].x, seg[k].y);
        prevCubicCtrl = { x: x2, y: y2 };
        prevQuadCtrl = null;
        cur = { x, y };
        break;
      }
      case 'Q': {
        let x1 = tokens[i++], y1 = tokens[i++];
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x1 += cur.x; y1 += cur.y; x += cur.x; y += cur.y; }
        const seg = flattenQuadratic(cur.x, cur.y, x1, y1, x, y, flatness);
        for (let k = 1; k < seg.length; k++) pushPt(seg[k].x, seg[k].y);
        prevQuadCtrl = { x: x1, y: y1 };
        prevCubicCtrl = null;
        cur = { x, y };
        break;
      }
      case 'T': {
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x += cur.x; y += cur.y; }
        const x1 = prevQuadCtrl ? 2 * cur.x - prevQuadCtrl.x : cur.x;
        const y1 = prevQuadCtrl ? 2 * cur.y - prevQuadCtrl.y : cur.y;
        const seg = flattenQuadratic(cur.x, cur.y, x1, y1, x, y, flatness);
        for (let k = 1; k < seg.length; k++) pushPt(seg[k].x, seg[k].y);
        prevQuadCtrl = { x: x1, y: y1 };
        prevCubicCtrl = null;
        cur = { x, y };
        break;
      }
      case 'A': {
        const rx = tokens[i++], ry = tokens[i++];
        const rot = tokens[i++];
        const laf = tokens[i++], sf = tokens[i++];
        let x = tokens[i++], y = tokens[i++];
        if (isRel) { x += cur.x; y += cur.y; }
        const seg = flattenArc(cur.x, cur.y, rx, ry, rot, laf, sf, x, y, flatness);
        for (let k = 1; k < seg.length; k++) pushPt(seg[k].x, seg[k].y);
        prevCubicCtrl = null; prevQuadCtrl = null;
        cur = { x, y };
        break;
      }
      default:
        // Unknown, skip a number to avoid infinite loop
        i++;
    }
  }
  if (current.length) subpaths.push(current);
  return subpaths.map(annotateSubpath);
}

function annotateSubpath(pts) {
  // Tag subpath as closed if first and last are coincident.
  const closed = pts.length > 1 &&
    Math.abs(pts[0].x - pts[pts.length - 1].x) < 1e-6 &&
    Math.abs(pts[0].y - pts[pts.length - 1].y) < 1e-6;
  pts.closed = closed;
  return pts;
}

function sampleCircle(cx, cy, r, n = 64) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * 2 * Math.PI;
    pts.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
  }
  pts.closed = true;
  return pts;
}

function sampleEllipse(cx, cy, rx, ry, n = 64) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * 2 * Math.PI;
    pts.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
  }
  pts.closed = true;
  return pts;
}

function sampleRect(x, y, w, h, rx = 0, ry = 0) {
  // Ignore rounded corners for now — tiny accuracy loss
  const pts = [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
    { x, y },
  ];
  pts.closed = true;
  return pts;
}

function parsePolyPoints(str, closed) {
  const nums = str.trim().split(/[\s,]+/).filter(Boolean).map(parseFloat);
  const pts = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    pts.push({ x: nums[i], y: nums[i + 1] });
  }
  if (closed && pts.length > 0) {
    pts.push({ ...pts[0] });
  }
  pts.closed = closed;
  return pts;
}

/**
 * Parse SVG string → { subpaths, bbox }.
 * subpaths: array of array of {x,y}; each has .closed flag.
 */
export function parseSVG(svg, opts = {}) {
  const flatness = opts.flatness ?? 0.5;
  const polylineSamples = opts.polylineSamples ?? 64;
  const subpaths = [];

  // <path d="...">
  const pathRe = /<path\b[^>]*\bd\s*=\s*["']([^"']+)["'][^>]*>/gi;
  for (const m of svg.matchAll(pathRe)) {
    subpaths.push(...flattenPath(m[1], flatness));
  }

  // <circle cx cy r>
  const circleRe = /<circle\b([^>]*?)\/?>/gi;
  for (const m of svg.matchAll(circleRe)) {
    const a = parseAttrs(m[1]);
    const r = parseFloat(a.r);
    if (Number.isFinite(r) && r > 0) {
      subpaths.push(sampleCircle(parseFloat(a.cx) || 0, parseFloat(a.cy) || 0, r, polylineSamples));
    }
  }

  // <ellipse cx cy rx ry>
  const ellipseRe = /<ellipse\b([^>]*?)\/?>/gi;
  for (const m of svg.matchAll(ellipseRe)) {
    const a = parseAttrs(m[1]);
    const rx = parseFloat(a.rx), ry = parseFloat(a.ry);
    if (Number.isFinite(rx) && Number.isFinite(ry)) {
      subpaths.push(sampleEllipse(parseFloat(a.cx) || 0, parseFloat(a.cy) || 0, rx, ry, polylineSamples));
    }
  }

  // <rect x y width height>
  const rectRe = /<rect\b([^>]*?)\/?>/gi;
  for (const m of svg.matchAll(rectRe)) {
    const a = parseAttrs(m[1]);
    const w = parseFloat(a.width), h = parseFloat(a.height);
    if (Number.isFinite(w) && Number.isFinite(h)) {
      subpaths.push(sampleRect(parseFloat(a.x) || 0, parseFloat(a.y) || 0, w, h));
    }
  }

  // <polygon points="...">, <polyline points="...">
  const polyRe = /<(polygon|polyline)\b([^>]*)>/gi;
  for (const m of svg.matchAll(polyRe)) {
    const a = parseAttrs(m[2]);
    if (a.points) {
      subpaths.push(parsePolyPoints(a.points, m[1].toLowerCase() === 'polygon'));
    }
  }

  // <line x1 y1 x2 y2>
  const lineRe = /<line\b([^>]*?)\/?>/gi;
  for (const m of svg.matchAll(lineRe)) {
    const a = parseAttrs(m[1]);
    const x1 = parseFloat(a.x1), y1 = parseFloat(a.y1);
    const x2 = parseFloat(a.x2), y2 = parseFloat(a.y2);
    if ([x1, y1, x2, y2].every(Number.isFinite)) {
      const pts = [{ x: x1, y: y1 }, { x: x2, y: y2 }];
      pts.closed = false;
      subpaths.push(pts);
    }
  }

  const bbox = computeBBox(subpaths.flat());
  return { subpaths, bbox };
}

function computeBBox(pts) {
  if (pts.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
